/**
 * Hook to fetch marketing content from CMS API
 */
import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default content fallbacks (in case API fails)
const DEFAULT_CONTENT = {
  hero: {
    badge: "Bridge Physical to Digital",
    title: "Turn Any Object Into an Interactive Experience",
    subtitle: "NFC tags and QR codes that connect physical products to digital content. Track engagement, update content instantly, and create unforgettable moments.",
    cta_primary: "Request Demo",
    cta_secondary: "See How It Works",
    stats: {
      value: "500+",
      label: "Trusted by 500+ businesses"
    }
  },
  features: {
    title: "Everything You Need",
    subtitle: "Powerful features to connect your physical products with digital experiences",
    items: []
  },
  pricing: {
    title: "Simple, Transparent Pricing",
    subtitle: "Start free, scale as you grow",
    plans: []
  },
  testimonials: {
    title: "Loved by Businesses",
    items: []
  },
  contact: {
    title: "Get in Touch",
    subtitle: "Ready to transform your physical products into interactive experiences? Let's talk about your needs.",
    email: "andy@scanit2go.com",
    phone: "+44 7829 710 543",
    website: "www.scanit2go.com"
  },
  about_hero: {
    title: "About ScanIt2Go",
    subtitle: "We're on a mission to bridge the physical and digital worlds, creating meaningful connections through innovative technology."
  },
  about_story: {
    title: "Our Story",
    paragraphs: []
  }
};

/**
 * Hook to fetch all marketing content
 * @returns {Object} { content, loading, error, refetch }
 */
export const useMarketingContent = () => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${BACKEND_URL}/api/content`);
      // Merge with defaults to ensure all keys exist
      const merged = { ...DEFAULT_CONTENT };
      Object.keys(response.data).forEach(key => {
        merged[key] = { ...DEFAULT_CONTENT[key], ...response.data[key] };
      });
      setContent(merged);
    } catch (err) {
      console.error('Failed to fetch marketing content:', err);
      setError(err);
      // Keep default content on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  return { content, loading, error, refetch: fetchContent };
};

/**
 * Hook to fetch a single section's content
 * @param {string} sectionId - The section ID to fetch
 * @returns {Object} { content, loading, error }
 */
export const useSectionContent = (sectionId) => {
  const [content, setContent] = useState(DEFAULT_CONTENT[sectionId] || {});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSection = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${BACKEND_URL}/api/content/${sectionId}`);
        setContent({ ...DEFAULT_CONTENT[sectionId], ...response.data });
      } catch (err) {
        console.error(`Failed to fetch section ${sectionId}:`, err);
        setError(err);
        // Keep default content on error
      } finally {
        setLoading(false);
      }
    };

    fetchSection();
  }, [sectionId]);

  return { content, loading, error };
};

export default useMarketingContent;
